import React, { useEffect, useRef, useState } from "react";

import LectureRequest from "./LectureRequest";
import { SEO } from "../../seo/SEO";
import styled from "styled-components";
import { useTypedResource } from "../../i18n/languagehandler";

const HeroSection = styled.section`
  max-width: 1200px;
  margin: 40px auto 0;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 30px;
  align-items: start;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const TitleContainer = styled.div`
  grid-column: 1 / -1;
  margin-bottom: 20px;
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ImageContainer = styled.div<{ height?: number }>`
  display: flex;
  align-items: stretch;
  ${(props) => props.height && `height: ${props.height}px;`}
  min-height: 0;
`;

const ContentSection = styled.section`
  max-width: 1200px;
  margin: 60px auto 0;
  padding: 0 20px;
`;

const Title = styled.h2`
  color: var(--text);
  font-size: 36px;
  margin: 0 0 20px 0;
  line-height: 1.2;
  text-transform: uppercase;
`;

const Subtitle = styled.h3`
  color: var(--primary);
  font-size: 24px;
  margin: 30px 0 16px 0;
`;

const BodyText = styled.p`
  color: var(--text-secondary);
  font-size: 15px;
  line-height: 1.6;
  margin: 0 0 8px 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 24px;
  margin-top: 40px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 320px;
  object-fit: cover;
`;

const CardContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const CardTitle = styled.h3`
  color: var(--text);
  font-size: 20px;
  margin: 0 0 12px 0;
  line-height: 1.3;
`;

const CardTeaser = styled.p`
  color: var(--primary);
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  margin: 0 0 12px 0;
`;

const CardDescription = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 12px 0;
  white-space: pre-line;
`;

const CardAudience = styled.p`
  color: var(--text-secondary);
  font-size: 14px;
  font-style: italic;
  margin: 0 0 20px 0;
`;
const Toggle = styled.button`
  background: none;
  border: none;
  color: var(--primary);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  margin-top: 4px;
  display: block;
  margin-left: auto;
  margin-right: auto;
  &:hover {
    text-decoration: underline;
  }
`;

const ToggleContainer = styled.div`
  margin-top: auto;
  padding-top: 16px;
`;

// Buttons removed per request; cards now have no action buttons

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 16px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  display: block;
  @media (max-width: 900px) {
    height: 280px;
    object-fit: cover;
  }
`;

// Testimonials section styles
const TestimonialsSection = styled.section`
  max-width: 1200px;
  margin: 60px auto 0;
  padding: 0 20px;
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 24px;
  margin-top: 30px;
`;

const TestimonialCard = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
  padding: 24px;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  border-left: 4px solid var(--primary);
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  }
`;

const TestimonialText = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.6;
  margin: 0 0 16px 0;
  font-style: italic;
`;

const TestimonialAuthor = styled.p`
  color: var(--primary);
  font-size: 14px;
  font-weight: 600;
  margin: 0;
`;

export const Lectures: React.FC = () => {
  const res = useTypedResource<{
    title: string;
    intro: string;
    introDescription: string;
    introWork: string;
    introExpertise: string;
    introConsulting: string;
    card1Title: string;
    card1Teaser: string;
    card1Description: string;
    card1Audience: string;
    card1Extended: string;
    card2Title: string;
    card2Teaser: string;
    card2Description: string;
    card2Audience: string;
    card2Extended: string;
    card3Title: string;
    card3Teaser: string;
    card3Description: string;
    card3Audience: string;
    card3Extended: string;
    card4Title: string;
    card4Teaser: string;
    card4Description: string;
    card4Extended: string;
    card5Title: string;
    card5Teaser: string;
    card5Description: string;
    card5Extended: string;
    card6Title: string;
    card6Teaser: string;
    card6Description: string;
    card6Extended: string;
    showMore: string;
    showLess: string;
    learnMoreButton: string;
    testimonialsTitle: string;
    testimonial1: string;
    testimonial1Author: string;
    testimonial2: string;
    testimonial2Author: string;
    testimonial3: string;
    testimonial3Author: string;
  }>("lectures");

  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const textContentRef = useRef<HTMLDivElement>(null);
  const [imageHeight, setImageHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const updateHeight = () => {
      if (textContentRef.current) {
        const height = textContentRef.current.offsetHeight;
        setImageHeight(height);
      }
    };

    // Use setTimeout to ensure DOM is fully rendered
    const timeoutId = setTimeout(updateHeight, 0);
    window.addEventListener("resize", updateHeight);

    // Also update when window loads
    window.addEventListener("load", updateHeight);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("load", updateHeight);
    };
  }, [
    res.introDescription,
    res.introWork,
    res.introExpertise,
    res.introConsulting,
  ]);

  const toggleExpanded = (index: number) => {
    setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const lectures = [
    {
      title: res.card1Title,
      teaser: res.card1Teaser,
      description: res.card1Description,
      audience: res.card1Audience,
      extended: res.card1Extended,
      image: "/assets/Lectures/skaermbrug-boerns-udvikling.jpg",
    },
    {
      title: res.card4Title,
      teaser: res.card4Teaser,
      description: res.card4Description,
      extended: res.card4Extended,
      image: "/assets/Lectures/Lecture 4.jpeg",
    },
    {
      title: res.card3Title,
      teaser: res.card3Teaser,
      description: res.card3Description,
      audience: res.card3Audience,
      extended: res.card3Extended,
      image: "/assets/Lectures/Lecture 3.png",
    },
    {
      title: res.card2Title,
      teaser: res.card2Teaser,
      description: res.card2Description,
      audience: res.card2Audience,
      extended: res.card2Extended,
      image: "/assets/Lectures/smaaboern-skaermbrug.jpg",
    },
    {
      title: res.card5Title,
      teaser: res.card5Teaser,
      description: res.card5Description,
      extended: res.card5Extended,
      image: "/assets/Lectures/Lecture 5.png",
    },
    {
      title: res.card6Title,
      teaser: res.card6Teaser,
      description: res.card6Description,
      extended: res.card6Extended,
      image: "/assets/Lectures/regulere-folelser.jpg",
    },
  ];

  return (
    <main>
      <SEO
        title={res.title}
        description={res.introDescription}
        path="/lectures"
      />

      <HeroSection>
        <TitleContainer>
          <Title>{res.title}</Title>
        </TitleContainer>
        <TextContent ref={textContentRef}>
          <BodyText>{res.introDescription}</BodyText>
          <BodyText>{res.introWork}</BodyText>
          <BodyText>{res.introExpertise}</BodyText>
          <BodyText>{res.introConsulting}</BodyText>
        </TextContent>
        <ImageContainer height={imageHeight}>
          <HeroImage
            src={process.env.PUBLIC_URL + "/assets/Lectures/On stage-57.jpeg"}
            alt="Lectures"
          />
        </ImageContainer>
      </HeroSection>

      <TestimonialsSection>
        <Subtitle>{res.testimonialsTitle}</Subtitle>
        <TestimonialsGrid>
          <TestimonialCard>
            <TestimonialText>"{res.testimonial1}"</TestimonialText>
            <TestimonialAuthor>— {res.testimonial1Author}</TestimonialAuthor>
          </TestimonialCard>
          <TestimonialCard>
            <TestimonialText>"{res.testimonial2}"</TestimonialText>
            <TestimonialAuthor>— {res.testimonial2Author}</TestimonialAuthor>
          </TestimonialCard>
          <TestimonialCard>
            <TestimonialText>"{res.testimonial3}"</TestimonialText>
            <TestimonialAuthor>— {res.testimonial3Author}</TestimonialAuthor>
          </TestimonialCard>
        </TestimonialsGrid>
      </TestimonialsSection>

      <ContentSection>
        <Grid>
          {lectures.map((lecture, index) => (
            <Card key={index}>
              <CardImage
                src={process.env.PUBLIC_URL + lecture.image}
                alt={lecture.title}
              />
              <CardContent>
                <CardTitle>{lecture.title}</CardTitle>
                <CardTeaser>{lecture.teaser}</CardTeaser>
                <CardDescription>{lecture.description}</CardDescription>
                {lecture.audience && (
                  <CardAudience>{lecture.audience}</CardAudience>
                )}
                {lecture.audience && (
                  <CardAudience>{lecture.audience}</CardAudience>
                )}
                {lecture.extended && (
                  <>
                    {expanded[index] && (
                      <CardDescription>{lecture.extended}</CardDescription>
                    )}
                    <ToggleContainer>
                      <Toggle onClick={() => toggleExpanded(index)}>
                        {expanded[index] ? res.showLess : res.showMore}
                      </Toggle>
                    </ToggleContainer>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </Grid>
      </ContentSection>

      <ContentSection>
        <LectureRequest />
      </ContentSection>
    </main>
  );
};
